import React, { FC } from 'react';
import { TContentHubHeroBannerSeriesDetailProps } from './types/productHeroBannerSeriesDetail.types';
import { isEditorActive, RichText, Text } from '@sitecore-jss/sitecore-jss-react';
import { ContentHubHeroBannerSeriesDetailModel } from './models/productHeroBannerSeriesDetail.model';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import useExperiencerEditorPlaceholder from '../../hooks/useExperiencerEditorPlaceholder';
import { BreakpointsInPx } from '../../constants/breakpoints.constants';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import { targetLinkdsk } from '../../utils/targetDskLink';
import CTAButton, { ctaEnums } from '../@shared/ctaButton';
import RoutableNotEditableLink from '../@shared/routableNotEditableLink';
import Picture from '../@shared/picture';
import FadeDownEffect from '../@shared/fadeDownEffect';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';
import { googleClickCtaEvent } from '../../utils/analytics';
import useGetBuildAndPriceLink from '../../hooks/useGetBuildAndPriceLink';
import DictionaryTerm from '../@shared/dictionaryTerm';
import { appendRootToInternalLink } from '../../utils/appendRootToInternalLink.utils';

const ProductHeroBannerSeriesDetail: FC<TContentHubHeroBannerSeriesDetailProps> = (props) => {
  const model = new ContentHubHeroBannerSeriesDetailModel(props);
  const apiResponse = isEmptyResponse(props);
  const isPlaceholderShown = useExperiencerEditorPlaceholder(props);
  const isMobile = useMediaQuery(`(max-width: ${BreakpointsInPx['md']})`);
  const buildAndPriceUrl = useGetBuildAndPriceLink('series');

  const clickHeroCtaEvent = (label: string | undefined) => {
    googleClickCtaEvent('Hero', label, model.title);
  };

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  return (
    <AnalyticsVisibleEvent rendering={props?.rendering} title={model.title} template="Series Detail Hero Banner">
      <div
        className={`hero-banner hero-banner--series-detail ${!model?.isGradientDisabled ? 'hero-banner--background' : ''}`}
        data-text-color={model.textColor?.toLowerCase()}
      >
        <div className="hero-banner__content hero-banner__content--align-top">
          <FadeDownEffect delay={1000}>
            <Text
              tag="p"
              field={{ value: model.eyebrow }}
              className={`hero-banner__eyebrow hero-banner--${model.textColor}`}
              editable={false}
            />
          </FadeDownEffect>
          <FadeDownEffect delay={2000}>
            <Text
              tag="h1"
              field={{ value: model.title }}
              className={`hero-banner__title hero-banner--${model.textColor}`}
              editable={false}
            />
          </FadeDownEffect>
          {model.labelCost && model.price && (
            <FadeDownEffect delay={2500}>
              <>
                <RichText
                  tag="div"
                  field={{ value: model.labelCost + ' ' + model.price }}
                  className={`hero-banner__price-label hero-banner--${model.textColor} rich-text`}
                  editable={false}
                />
              </>
            </FadeDownEffect>
          )}
          {
            <FadeDownEffect delay={3000}>
              <RichText
                tag="div"
                field={{ value: model.description }}
                className={`hero-banner__description hero-banner--${model.textColor} rich-text`}
                editable={false}
              />
            </FadeDownEffect>
          }
          {!isMobile ? (
            <FadeDownEffect delay={4000}>
              <>
                {model?.enableBuildPriceCTA && buildAndPriceUrl && (
                  <CTAButton type={ctaEnums.primaryRed}>
                    <RoutableNotEditableLink
                      className={'hero-banner__link hero-banner__link--primary'}
                      href={appendRootToInternalLink(buildAndPriceUrl)}
                    >
                      <DictionaryTerm keyword={'cnhi-build-price'} />
                    </RoutableNotEditableLink>
                  </CTAButton>
                )}
                {model?.primaryLink?.label && model?.primaryLink?.url && (
                  <CTAButton type={ctaEnums.primaryRed}>
                    <RoutableNotEditableLink
                      className={'hero-banner__link hero-banner__link--primary'}
                      href={model.primaryLinkUrl}
                      target={targetLinkdsk({ value: { href: model?.primaryLink?.url, target: model?.primaryLink?.target } }, !isMobile)}
                      onClick={() => clickHeroCtaEvent(model?.primaryLink?.label)}
                    >
                      {model.primaryLink.label}
                    </RoutableNotEditableLink>
                  </CTAButton>
                )}
                {model?.secondaryLink?.label && model?.secondaryLink?.url && (
                  <CTAButton type={ctaEnums.primaryWhite}>
                    <RoutableNotEditableLink
                      className={'hero-banner__link hero-banner__link--secondary'}
                      href={model.secondaryLinkUrl}
                      target={targetLinkdsk(
                        { value: { href: model?.secondaryLink?.url, target: model?.secondaryLink?.target } },
                        !isMobile
                      )}
                      onClick={() => clickHeroCtaEvent(model?.secondaryLink?.label)}
                    >
                      {model.secondaryLink.label}
                    </RoutableNotEditableLink>
                  </CTAButton>
                )}
              </>
            </FadeDownEffect>
          ) : null}
        </div>
        {/* Need to update useHeroBanner hook if the images sent to Picture component  are changed */}
        <Picture
          productImageProps={{
            desktopRendition: model.desktopImage,
            desktopSDRendition: model.desktopSDImage,
            tabletRendition: model.tabletImage,
            tabletLandscapeRendition: model.tabletLandscapeImage,
            mobileRendition: model.mobileImage,
            alt: model?.imageAlt,
            imageClassName: 'hero-banner__image hero-banner__image--desktop',
            prioritize: true,
          }}
        />
        <FadeDownEffect delay={5000}>
          <div className="hero-banner__content hero-banner__specs-fade-wrapper hero-banner__content--align-bottom hero-banner__content--align-center">
            <div className={`hero-banner__specs-wrapper hero-banner__specs-wrapper--${model.quickSpecsColor}`}>
              {model.summarySpecs?.map((item) => (
                <div className={`hero-banner__specs hero-banner__specs--${model.quickSpecsColor}`}>
                  <RichText
                    field={{ value: item.key }}
                    className={`hero-banner__label hero-banner--${model.quickSpecsColor} rich-text`}
                  ></RichText>
                  <RichText
                    field={{ value: item.value }}
                    className={`hero-banner__value hero-banner--${model.quickSpecsColor} rich-text`}
                  ></RichText>
                </div>
              ))}
            </div>
          </div>
        </FadeDownEffect>
      </div>
      {isMobile ? (
        <div className="hero-banner__mobile-only">
          {model?.enableBuildPriceCTA && buildAndPriceUrl && (
            <CTAButton type={ctaEnums.primaryRed}>
              <RoutableNotEditableLink
                className={'hero-banner__link hero-banner__link--primary'}
                href={appendRootToInternalLink(buildAndPriceUrl)}
              >
                <DictionaryTerm keyword={'cnhi-build-price'} />
              </RoutableNotEditableLink>
            </CTAButton>
          )}
          {model?.primaryLink?.label && model?.primaryLink?.url && (
            <CTAButton type={ctaEnums.primaryRed}>
              <RoutableNotEditableLink
                className="hero-banner__link hero-banner__link--primary"
                href={model.primaryLinkUrl}
                onClick={() => clickHeroCtaEvent(model?.primaryLink?.label)}
              >
                {model.primaryLink.label}
              </RoutableNotEditableLink>
            </CTAButton>
          )}
          {model?.secondaryLink?.label && model?.secondaryLink?.url && (
            <CTAButton type={ctaEnums.primaryWhite}>
              <RoutableNotEditableLink
                className={'hero-banner__link hero-banner__link--secondary'}
                href={model.secondaryLinkUrl}
                onClick={() => clickHeroCtaEvent(model?.secondaryLink?.label)}
              >
                {model.secondaryLink.label}
              </RoutableNotEditableLink>
            </CTAButton>
          )}
        </div>
      ) : null}
    </AnalyticsVisibleEvent>
  );
};

export default ProductHeroBannerSeriesDetail;
